/**
 * Opening-URL router (replaces the old navigo-backed RouteManager).
 *
 * This router only resolves the URL the site was OPENED on: init() matches
 * location.pathname against the route table once and calls the matched
 * controller's function (default 'init') with any captured params — exactly
 * the first-resolve behaviour of the old RouteManager, which paused navigo
 * after the opening route (openingRouteOnly). In-session navigation is not
 * routed here; it is handled by HistoryController + events.
 *
 * Matching: leading/trailing slashes, hash and query string are ignored.
 * Static segments must match exactly; ':param' segments capture the segment
 * into the params object under the param name (e.g. 'project/:projectslug'
 * against '/project/toy-town' -> { projectslug: 'toy-town' }).
 * No match = do nothing (the old notFound handler was silent).
 */
import { ControllerManager } from './ControllerManager';

interface RouteConfig {
	path: string;
	controller: string;
	function?: string;
}

export class RouteManager {
	public openingRouteOnly: boolean = true;

	private _routes: RouteConfig[] = [];
	private _controllerManager: ControllerManager;

	public set controllerManager(manager: ControllerManager) {
		this._controllerManager = manager;
	}

	public set(routes: RouteConfig[]): void {
		this._routes = routes;
	}

	public init(): void {
		if (this._routes.length) {
			this._resolveOpeningRoute();
		}
	}

	//Match the opening pathname against the route table and fire the
	//controller function once (opening route only — see file header).
	private _resolveOpeningRoute(): void {
		let path = this._cleanPath(window.location.pathname);

		for (let i = 0; i < this._routes.length; i++) {
			let params = this._match(this._routes[i].path, path);

			if (params) {
				let controller = this._controllerManager.get(this._routes[i].controller);
				let func = this._routes[i].function ? this._routes[i].function : 'init';
				//navigo passed null (not {}) to handlers of param-less routes:
				controller[func](Object.keys(params).length ? params : null);
				return;
			}
		}

		//No match: silent, like the old navigo notFound handler.
	}

	//Strip leading/trailing slashes (hash/query never appear in pathname).
	private _cleanPath(path: string): string {
		return path.replace(/^\/+|\/+$/g, '');
	}

	//Segment-match a route pattern against a cleaned path.
	//Returns a params object on match (empty for static routes), null on no match.
	private _match(pattern: string, path: string): { [key: string]: string } | null {
		let patternSegments = this._cleanPath(pattern).split('/');
		let pathSegments = path.split('/');

		//'/' cleans to '' which splits to [''] — matches the empty path only.
		if (patternSegments.length !== pathSegments.length) {
			return null;
		}

		let params: { [key: string]: string } = {};

		for (let i = 0; i < patternSegments.length; i++) {
			if (patternSegments[i].charAt(0) === ':') {
				params[patternSegments[i].substring(1)] = pathSegments[i];
			} else if (patternSegments[i] !== pathSegments[i]) {
				return null;
			}
		}

		return params;
	}
}
